// University of Houston: public college page (#2748), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - UH's Common Data Set 2025-2026 (common_data_set_2025-2026_final.xlsx),
//    linked from Institutional Research's Common Data Sets page: C1
//    applicants, admits and enrollees for Fall 2025 with the residency
//    table; C8 test policy; C21/C22 plans (UH has neither early decision nor
//    early action); G1/G5 costs. Quirk: G1 asks for 2026-2027 costs and UH
//    also ticked the box saying its final 2026-2027 costs were not yet
//    available. Its tuition and required fees ($10,856 plus $1,032) equal
//    the $11,888 that UH Admissions gives for 2026-27 in-state tuition and
//    fees; the other items are the CDS's estimates. UH's financial aid site
//    posts no cost-of-attendance table (it points to the net price
//    calculator), so the CDS items are used.
//  - UH Undergraduate Admissions: Freshman Admissions Process (Fall 2027
//    deadlines table), Test Optional Admissions, and Freshman Majors with
//    Additional Requirements (a PDF headed "Requirements for spring, summer
//    and fall 2027 admission terms"). UH does not publish decision dates.
//  - College Scorecard, school id 225511 (net price by family income), read
//    from the Scorecard API on 2026-10-01.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'UH Common Data Set 2025-2026',
        url: 'https://www.uh.edu/ir/reports/common-data-sets/index.php',
        read: '2026-10-01',
    },
    process: {
        label: 'UH Undergraduate Admissions, Freshman Admissions Process',
        url: 'https://www.uh.edu/undergraduate-admissions/apply/incoming-freshman/freshman-admissions-process/',
        read: '2026-10-01',
    },
    tests: {
        label: 'UH Undergraduate Admissions, Test Optional Admissions',
        url: 'https://www.uh.edu/undergraduate-admissions/resources/test-optional-admissions/',
        read: '2026-10-01',
    },
    majorReqs: {
        label: 'UH Undergraduate Admissions, Freshman Majors with Additional Requirements (fall 2027)',
        url: 'https://www.uh.edu/undergraduate-admissions/academics/_files/additional-requirements-freshman.pdf',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?225511-University-of-Houston',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'university-of-houston',
    kbId: 'university_of_houston',
    name: 'University of Houston',
    shortName: 'Houston',
    aliases: ['UH'],
    location: 'Houston, TX',
    control: 'Public',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (UH brand style guide); decoration only.
    branding: {
        primary: '#C8102E',
        secondary: '#FFFFFF',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/university_of_houston_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 34728,
        admitted: 26312,
        enrolled: 6669,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 28115, admitted: 21788 },
            { group: 'Out-of-state', applied: 4866, admitted: 3033 },
            { group: 'International', applied: 1747, admitted: 1491 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'UH\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by college or major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early plans',
        note: 'UH has no early decision or early action plan. Its November 2 scholarship priority deadline is an earlier filing date, not an early decision.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'You apply to UH with a major. These majors ask for more than the university\'s own requirements:',
        rows: [
            { school: 'Architecture and Design', requirement: 'Fall entry only. Every applicant sends a portfolio by the February 1 priority deadline.' },
            { school: 'College of the Arts', requirement: 'A separate College of the Arts application. Music, theatre and dance applicants also audition; art, graphic design, painting, photography/video and sculpture applicants send a portfolio; art history applicants send an essay.' },
            { school: 'Bauer College of Business', requirement: 'Automatic admission with one of: top 5% of your class, a 3.9 core GPA, or a 1350 SAT or 29 ACT with at least a 3.8 core GPA.' },
            { school: 'Cullen College of Engineering', requirement: 'One of: top 10% of your class, a 3.8 core GPA, or a 1400 SAT or 32 ACT with at least a 3.0 core GPA.' },
            { school: 'Natural Sciences and Mathematics', requirement: 'Automatic admission with one of: top 10%, a 3.8 core GPA, or a 1400 SAT or 32 ACT with at least a 3.0 core GPA. You also need 2 units of biology, chemistry or physics, 4 of English, 2 of algebra, 1 of geometry and 1 of precalculus.' },
            { school: 'Nursing', requirement: 'You apply as Pre-Nursing. Automatic admission with one of: top 10%, a 3.8 core GPA, or a 1300 SAT or 27 ACT, plus 1 unit of biology, 2 more science units, 1 of algebra, 1 of geometry, 1 more math unit and 3 of English.' },
        ],
        source: 'majorReqs',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Fall 2027 application deadline', value: 'May 31, 2027' },
        rows: [
            { plan: 'Scholarship priority deadline', deadline: 'November 2, 2026', decision: null, binding: false },
            { plan: 'Fall 2027 (U.S. freshman)', deadline: 'May 31, 2027', decision: null, binding: false },
        ],
        note: 'The application opens August 1, 2026, and the fee is $75. Supporting information (the fee, the transcript grades you enter yourself and any test scores) is due November 9, 2026 for the scholarship priority deadline and June 7, 2027 for the final deadline. UH does not publish decision dates.',
        source: 'process',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'UH is test-optional through June 1, 2030, which covers every term through fall 2030. You choose on the application whether you are reviewed with or without scores, and you are not disadvantaged without them. If you send scores, UH uses your highest total or composite and does not superscore; scores may be no more than five years old. A 1250 SAT or 26 ACT is one of three ways to earn assured admission, alongside top 10% of your class or a 3.5 unweighted GPA in English, math, science and social studies.',
        source: 'tests',
        cdsLine: 'The Common Data Set also says UH is test-optional and that applicants decide whether their score is used, with scores due by June 1.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'tests',
    },

    cost: {
        year: '2026-2027',
        columns: ['In-state', 'Out-of-state'],
        rows: [
            { item: 'Tuition', amounts: [10856, 26744] },
            { item: 'Required fees', amounts: [1032, 1032] },
            { item: 'Food and housing (on campus)', amounts: [11286, 11286] },
            { item: 'Books and supplies', amounts: [1430, 1430] },
            { item: 'Transportation', amounts: [1439, 1439] },
            { item: 'Other expenses', amounts: [3889, 3889] },
        ],
        totals: [29932, 45820],
        note: 'For a full-time first-year student living on campus. When UH filed these figures it marked its final 2026-2027 costs as not yet available, so treat them as estimates. Tuition varies by college.',
        source: 'cds',
        where: 'sections G1 and G5',
        netPrice: {
            average: 11439,
            byIncome: [
                { income: '$0 to $30,000', amount: 8468 },
                { income: '$30,001 to $48,000', amount: 8553 },
                { income: '$48,001 to $75,000', amount: 8529 },
                { income: '$75,001 to $110,000', amount: 13799 },
                { income: 'Over $110,000', amount: 21065 },
            ],
            note: 'What first-year, full-time, in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from UH\'s aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
